import { useEffect, useState, useMemo } from "react";
import { api, TRAMITES, TRAMITE_LIST, MXN } from "@/lib/api";
import { toast } from "sonner";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { CircleDollarSign, Eye, Save, BarChart3 } from "lucide-react";
import CobranzaChart from "@/components/CobranzaChart";

const MESES = Array.from({ length: 18 }, (_, i) => i + 1);
const sumVals = (obj) => Object.values(obj || {}).reduce((a, b) => a + Number(b || 0), 0);
const isMod = (k) => k === "MOD_10" || k === "MOD_40";

export default function Cobranza() {
  const [tramiteTab, setTramiteTab] = useState(TRAMITE_LIST[0].key);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [chartOpen, setChartOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/clients", { params: { tramite: tramiteTab } });
      setClients(r.data);
    } catch {
      toast.error("Error al cargar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tramiteTab]);

  const t = TRAMITES[tramiteTab];

  return (
    <section data-testid="cobranza-section">
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl font-extrabold tracking-tight text-black">Cobranza</h1>
          <p className="text-neutral-500 mt-1 text-sm">Controla los pagos de cada cliente separados por tipo de trámite.</p>
        </div>
        <Button data-testid="cob-chart-btn" onClick={() => setChartOpen(true)} className="bg-black text-yellow-400 hover:bg-neutral-900 rounded-full">
          <BarChart3 className="w-4 h-4 mr-1.5" /> Ver gráfica
        </Button>
      </div>

      <Tabs value={tramiteTab} onValueChange={setTramiteTab}>
        <TabsList className="bg-white border border-neutral-200 h-auto flex-wrap p-1 gap-1">
          {TRAMITE_LIST.map((tr) => (
            <TabsTrigger
              key={tr.key}
              value={tr.key}
              data-testid={`cob-tab-${tr.key}`}
              className="rounded-lg data-[state=active]:text-white"
              style={tramiteTab === tr.key ? { backgroundColor: tr.color, color: "#fff" } : {}}
            >
              <span className={`inline-block w-2 h-2 rounded-full mr-1.5 ${tr.dot}`} />
              {tr.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-6">
        {loading && <div className="text-neutral-500 text-sm">Cargando…</div>}
        {!loading && clients.length === 0 && (
          <div className="p-10 text-center bg-white border border-dashed border-neutral-300 rounded-2xl">
            <CircleDollarSign className="w-8 h-8 text-neutral-400 mx-auto" />
            <p className="mt-3 text-neutral-500 text-sm">No hay clientes asignados a este trámite todavía.</p>
          </div>
        )}
        <div className="grid gap-3">
          {clients.map((c) => (
            <CobranzaRow key={c.id} client={c} tramite={t} onOpen={() => setSelectedId(c.id)} />
          ))}
        </div>
      </div>

      {chartOpen && <CobranzaChart onClose={() => setChartOpen(false)} />}

      {selectedId && (
        <CobranzaModal
          clientId={selectedId}
          tramiteKey={tramiteTab}
          onClose={() => setSelectedId(null)}
          onSaved={load}
        />
      )}
    </section>
  );
}

function CobranzaRow({ client, tramite, onOpen }) {
  const isIncremento = client.tramite === "INCREMENTO";
  const mod = isMod(client.tramite);
  const sumaIncremento = sumVals(client.pagos_incremento);
  const sumaMeses = sumVals(client.pagos_meses);
  const totalIncremento =
    sumaIncremento + Number(client.interes || 0) * Number(client.pension_final || 0) + Number(client.honorarios || 0);

  return (
    <div className="bg-white border border-neutral-200 rounded-2xl p-4 sm:p-5 hover:shadow-md hover:border-yellow-500 transition">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-heading text-lg font-semibold text-black truncate">{client.nombre_completo}</h3>
            <span
              className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border"
              style={{ backgroundColor: `${tramite.color}12`, color: tramite.color, borderColor: `${tramite.color}55` }}
            >{tramite.label}</span>
            {(isIncremento || mod) && Number(client.meses_asignados) > 0 && (
              <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-neutral-100 text-neutral-600">
                {client.meses_asignados} meses
              </span>
            )}
          </div>
          <div className="text-xs text-neutral-500 font-mono mt-1">
            NSS {client.nss} · {client.zona}
          </div>
        </div>
        <div className="flex items-center gap-6">
          {isIncremento && (
            <>
              <MiniStat label="Suma pagos" value={MXN(sumaIncremento)} />
              <MiniStat label="Cap+Int+Hon" value={MXN(totalIncremento)} tint="yellow" />
            </>
          )}
          {mod && (
            <>
              <MiniStat label="Suma meses" value={MXN(sumaMeses)} />
              <MiniStat label="Total" value={MXN(client.cobro)} tint="yellow" />
            </>
          )}
          {!isIncremento && !mod && <MiniStat label="Cobro" value={MXN(client.cobro)} tint="yellow" />}
          <Button variant="outline" size="icon" onClick={onOpen}
            data-testid={`cob-open-${client.id}`}
            className="rounded-full h-10 w-10 border-neutral-300 hover:bg-yellow-50 hover:border-yellow-500 hover:text-black">
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function MiniStat({ label, value, tint }) {
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-wider text-neutral-400">{label}</div>
      <div className={`font-mono font-semibold ${tint === "yellow" ? "text-yellow-700" : "text-neutral-800"}`}>{value}</div>
    </div>
  );
}

function MesesSelect({ value, onChange, testId }) {
  return (
    <div className="max-w-xs">
      <Label>¿Cuántos meses se asignarán? (1 a 18)</Label>
      <Select value={value ? String(value) : ""} onValueChange={(v) => onChange(Number(v))}>
        <SelectTrigger data-testid={testId}><SelectValue placeholder="Selecciona los meses" /></SelectTrigger>
        <SelectContent>
          {MESES.map((n) => (
            <SelectItem key={n} value={String(n)}>{n} {n === 1 ? "mes" : "meses"}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function MesesGrid({ meses, pagos, onChange, prefix }) {
  if (!meses) {
    return (
      <p className="text-sm text-neutral-500 italic" data-testid={`${prefix}-sin-meses`}>
        Selecciona primero el número de meses para capturar los pagos.
      </p>
    );
  }
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2">
      {MESES.slice(0, meses).map((mes) => {
        const val = pagos?.[mes] ?? "";
        const has = Number(val) > 0;
        return (
          <div key={mes} className={`rounded-xl p-2.5 border transition ${has ? "bg-yellow-50 border-yellow-400" : "bg-white border-neutral-200"}`}>
            <div className="text-[10px] uppercase tracking-wider text-neutral-500">Mes {mes}</div>
            <Input
              data-testid={`${prefix}-${mes}`}
              type="number"
              step="0.01"
              value={val}
              onChange={(e) => onChange(mes, e.target.value)}
              placeholder="0.00"
              className="font-mono mt-1 h-9 text-sm"
            />
          </div>
        );
      })}
    </div>
  );
}

function TotalBox({ label, value, testId, strong }) {
  return (
    <div className={`rounded-xl p-3 border ${strong ? "bg-black border-black" : "bg-neutral-50 border-neutral-200"}`}>
      <div className={`text-[10px] uppercase tracking-wider ${strong ? "text-yellow-400" : "text-neutral-400"}`}>{label}</div>
      <div data-testid={testId} className={`font-mono text-lg font-bold ${strong ? "text-yellow-400" : "text-neutral-800"}`}>{value}</div>
    </div>
  );
}

function CobranzaModal({ clientId, tramiteKey, onClose, onSaved }) {
  const [client, setClient] = useState(null);
  const [saving, setSaving] = useState(false);
  const t = TRAMITES[tramiteKey];
  const isIncremento = tramiteKey === "INCREMENTO";
  const mod = isMod(tramiteKey);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/clients/${clientId}`);
        setClient({
          ...r.data,
          pagos_incremento: r.data.pagos_incremento || {},
          pagos_meses: r.data.pagos_meses || {},
          meses_asignados: r.data.meses_asignados || 0,
        });
      } catch {
        toast.error("No se pudo cargar el cliente");
        onClose();
      }
    })();
    // eslint-disable-next-line
  }, [clientId]);

  const patch = (p) => setClient((c) => ({ ...c, ...p }));
  const setPago = (field) => (mes, valor) =>
    setClient((c) => ({ ...c, [field]: { ...(c[field] || {}), [mes]: valor } }));

  const setMeses = (n) =>
    setClient((c) => {
      const trim = (obj) => Object.fromEntries(Object.entries(obj || {}).filter(([k]) => Number(k) <= n));
      return { ...c, meses_asignados: n, pagos_incremento: trim(c.pagos_incremento), pagos_meses: trim(c.pagos_meses) };
    });

  const sumIncremento = useMemo(() => sumVals(client?.pagos_incremento), [client]);
  const sumMeses = useMemo(() => sumVals(client?.pagos_meses), [client]);
  const interesTotal = Number(client?.interes || 0) * Number(client?.pension_final || 0);
  const capitalIncremento = sumIncremento;
  const sumatorioIncremento = capitalIncremento + interesTotal + Number(client?.honorarios || 0);
  const porcentaje = Number(client?.porcentaje || 0);
  const montoPorcentaje = (sumMeses * porcentaje) / 100;
  const totalMod = sumMeses + montoPorcentaje;

  const toNumMap = (obj) => Object.fromEntries(Object.entries(obj || {}).map(([k, v]) => [k, Number(v || 0)]));

  const save = async () => {
    if (!client) return;
    setSaving(true);
    try {
      let body;
      if (isIncremento) {
        body = {
          pension_inicial: Number(client.pension_inicial || 0),
          pension_final: Number(client.pension_final || 0),
          capital: Number(capitalIncremento.toFixed(2)),
          interes: Number(client.interes || 0),
          honorarios: Number(client.honorarios || 0),
          meses_asignados: Number(client.meses_asignados || 0),
          pagos_incremento: toNumMap(client.pagos_incremento),
        };
      } else if (mod) {
        body = {
          meses_asignados: Number(client.meses_asignados || 0),
          pagos_meses: toNumMap(client.pagos_meses),
          porcentaje,
          cobro: Number(totalMod.toFixed(2)),
        };
      } else {
        body = { cobro: Number(client.cobro || 0) };
      }
      await api.put(`/clients/${clientId}`, body);
      toast.success("Cobranza guardada");
      onSaved?.();
      onClose();
    } catch {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="cobranza-modal" className="max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">
            {client?.nombre_completo || "Cargando…"}
          </DialogTitle>
        </DialogHeader>

        {client && (
          <div className="space-y-6">
            <div
              className="p-3 rounded-xl border-l-4 flex items-center gap-3"
              style={{ borderColor: t.color, backgroundColor: `${t.color}12` }}
            >
              <span className={`inline-block w-2 h-2 rounded-full ${t.dot}`} />
              <span className="text-sm font-semibold" style={{ color: t.color }}>{t.label}</span>
              <span className="text-xs text-neutral-500 ml-auto font-mono">NSS {client.nss}</span>
            </div>

            {isIncremento && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <Label>Pensión inicial (MXN)</Label>
                    <Input data-testid="cob-pension-inicial" type="number" step="0.01" value={client.pension_inicial || ""} onChange={(e) => patch({ pension_inicial: e.target.value })} className="font-mono" />
                  </div>
                  <div>
                    <Label>Pensión final (MXN)</Label>
                    <Input data-testid="cob-pension-final" type="number" step="0.01" value={client.pension_final || ""} onChange={(e) => patch({ pension_final: e.target.value })} className="font-mono" />
                  </div>
                  <div>
                    <Label>Capital (MXN)</Label>
                    <Input data-testid="cob-capital" type="text" value={MXN(capitalIncremento)} readOnly className="font-mono bg-neutral-100 text-neutral-700" />
                    <p className="text-[10px] text-neutral-500 mt-1">Se calcula con la suma de los meses</p>
                  </div>
                  <div>
                    <Label>Interés (1 al 10)</Label>
                    <Select value={String(client.interes || "")} onValueChange={(v) => patch({ interes: Number(v) })}>
                      <SelectTrigger data-testid="cob-interes"><SelectValue placeholder="Selecciona" /></SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                          <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <div className="text-xs text-yellow-700 font-mono mt-1">
                      Interés × Pensión final = <span className="font-bold" data-testid="cob-interes-total">{MXN(interesTotal)}</span>
                    </div>
                  </div>
                  <div>
                    <Label>Honorarios (MXN)</Label>
                    <Input data-testid="cob-honorarios" type="number" step="0.01" value={client.honorarios || ""} onChange={(e) => patch({ honorarios: e.target.value })} className="font-mono" />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <TotalBox label="Capital (suma de meses)" value={MXN(capitalIncremento)} testId="cob-sum-capital" />
                  <TotalBox label="Interés" value={MXN(interesTotal)} testId="cob-sum-interes" />
                  <TotalBox label="Honorarios" value={MXN(client.honorarios)} testId="cob-sum-honorarios" />
                  <div className="sm:col-span-3">
                    <TotalBox label="Sumatorio: Capital + Interés + Honorarios" value={MXN(sumatorioIncremento)} testId="cob-sumatorio" strong />
                  </div>
                </div>

                <div className="space-y-4">
                  <MesesSelect value={client.meses_asignados} onChange={setMeses} testId="cob-meses-asignados" />
                  <div className="flex items-center justify-between">
                    <h4 className="font-heading font-semibold text-neutral-800">
                      Pagos mensuales {client.meses_asignados ? `(1 a ${client.meses_asignados})` : ""}
                    </h4>
                    <div className="text-right">
                      <div className="text-[10px] uppercase tracking-wider text-neutral-400">Suma total</div>
                      <div data-testid="cob-suma-total" className="font-mono text-lg font-bold text-yellow-700">{MXN(sumIncremento)}</div>
                    </div>
                  </div>
                  <MesesGrid meses={client.meses_asignados} pagos={client.pagos_incremento} onChange={setPago("pagos_incremento")} prefix="cob-mes" />
                </div>
              </>
            )}

            {mod && (
              <div className="space-y-5">
                <MesesSelect value={client.meses_asignados} onChange={setMeses} testId="cob-meses-asignados" />
                <div>
                  <h4 className="font-heading font-semibold text-neutral-800 mb-3">
                    Pagos mensuales {client.meses_asignados ? `(1 a ${client.meses_asignados})` : ""}
                  </h4>
                  <MesesGrid meses={client.meses_asignados} pagos={client.pagos_meses} onChange={setPago("pagos_meses")} prefix="cob-mod-mes" />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-end">
                  <TotalBox label="Suma de todos los meses" value={MXN(sumMeses)} testId="cob-mod-suma" />
                  <div>
                    <Label>Porcentaje (%)</Label>
                    <Input
                      data-testid="cob-porcentaje"
                      type="number"
                      step="0.01"
                      min="0"
                      value={client.porcentaje || ""}
                      onChange={(e) => patch({ porcentaje: e.target.value })}
                      placeholder="Ej. 10"
                      className="font-mono"
                    />
                    <div className="text-xs text-yellow-700 font-mono mt-1">
                      {porcentaje}% de {MXN(sumMeses)} = <span className="font-bold" data-testid="cob-monto-porcentaje">{MXN(montoPorcentaje)}</span>
                    </div>
                  </div>
                </div>
                <TotalBox label="Total: suma de meses + porcentaje" value={MXN(totalMod)} testId="cob-mod-total" strong />
                <p className="text-xs text-neutral-500">Este total se guardará como el Cobro del cliente.</p>
              </div>
            )}

            {!isIncremento && !mod && (
              <div className="space-y-2">
                <Label>Cobro (MXN)</Label>
                <Input
                  data-testid="cob-cobro"
                  type="number"
                  step="0.01"
                  value={client.cobro || ""}
                  onChange={(e) => patch({ cobro: e.target.value })}
                  className="font-mono max-w-xs"
                />
                <p className="text-xs text-yellow-700 font-mono">{MXN(client.cobro)}</p>
              </div>
            )}

            <div className="flex justify-end pt-2 border-t">
              <Button variant="ghost" onClick={onClose}>Cancelar</Button>
              <Button data-testid="cob-save" onClick={save} disabled={saving} className="ml-2 bg-black text-yellow-400 hover:bg-neutral-900">
                <Save className="w-4 h-4 mr-1.5" />
                {saving ? "Guardando…" : "Guardar cobranza"}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
